import { AsyncPipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StyleClassModule } from 'primeng/styleclass';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [BreadcrumbModule, StyleClassModule, AsyncPipe],
})
export class BreadcrumbComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);

  static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
  menuItems = new BehaviorSubject<MenuItem[]>([]);

  ngOnInit(): void {
    void this.refreshBreadcrumbs();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => void this.refreshBreadcrumbs());
  }

  private async refreshBreadcrumbs(): Promise<void> {
    let bc = await this.createBreadcrumbs(this.activatedRoute.root);
    bc = bc.filter(
      (v, i, a) => a.findIndex((v2) => v2.label === v.label) === i,
    );
    bc = await this.prependAdminSettings(bc);
    this.menuItems.next(bc);
  }

  private async prependAdminSettings(bc: MenuItem[]): Promise<MenuItem[]> {
    const url = this.router.url.split('?')[0];
    if (!url.startsWith('/admin/') || url === '/admin') {
      return bc;
    }

    const [settingsLabel, adminLabel, templateMgmtLabel] = await Promise.all([
      firstValueFrom(this.translate.get('BREADCRUM.LABELS.ADMIN_SETTINGS')),
      firstValueFrom(this.translate.get('BREADCRUM.LABELS.ADMIN')),
      firstValueFrom(this.translate.get('BREADCRUM.LABELS.TEMPLATE_MANAGEMENT')),
    ]);

    bc = bc.filter(
      (item) =>
        item.label !== adminLabel && item.label !== templateMgmtLabel,
    );

    if (!bc.some((item) => item.label === settingsLabel)) {
      bc.unshift({ label: settingsLabel, routerLink: ['/admin'] });
    }

    return bc;
  }

  private async createBreadcrumbs(
    route: ActivatedRoute,
    url = '',
    breadcrumbs: MenuItem[] = [],
  ): Promise<MenuItem[]> {
    const children: ActivatedRoute[] = route.children;
    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url
        .map((segment) => segment.path)
        .join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data[BreadcrumbComponent.ROUTE_DATA_BREADCRUMB];

      if (label) {
        const translation = await firstValueFrom(
          this.translate.get(`BREADCRUM.LABELS.${label}`),
        );
        breadcrumbs.push({ label: translation, routerLink: [url] });
      }
      return this.createBreadcrumbs(child, url, breadcrumbs);
    }
    return breadcrumbs;
  }
}
